import React, { useState, useEffect } from 'react';
import { LuPlus, LuTrash2, LuSave, LuListTodo, LuLock } from 'react-icons/lu';
import { settingsService, SETTINGS_KEYS } from '../services/settingsService';
import { OPPORTUNITY_STAGES } from '../config/supabase';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

const Settings = () => {
    const { userProfile } = useAuth();
    const [checklists, setChecklists] = useState({});
    const [activeStage, setActiveStage] = useState(OPPORTUNITY_STAGES.LEAD);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newItem, setNewItem] = useState('');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        const result = await settingsService.getSettings(SETTINGS_KEYS.OPPORTUNITY_CHECKLISTS);
        if (result.success && result.data) {
            setChecklists(result.data);
        } else {
            // Fallback or empty (though migration should populate it)
            setChecklists({});
        }
        setLoading(false);
    };

    const handleAddItem = (e) => {
        e.preventDefault();
        if (!newItem.trim()) return;

        const currentList = checklists[activeStage] || [];
        const updatedList = [...currentList, newItem.trim()];
        const updatedChecklists = { ...checklists, [activeStage]: updatedList };

        setChecklists(updatedChecklists);
        setNewItem('');
        // Auto-save or wait for manual save? Manual save is safer for bulk edits.
    };

    const handleRemoveItem = (index) => {
        const currentList = checklists[activeStage] || [];
        const updatedList = currentList.filter((_, i) => i !== index);
        const updatedChecklists = { ...checklists, [activeStage]: updatedList };
        setChecklists(updatedChecklists);
    };

    const handleUpdateItem = (index, value) => {
        const currentList = checklists[activeStage] || [];
        const updatedList = [...currentList];
        updatedList[index] = value;
        const updatedChecklists = { ...checklists, [activeStage]: updatedList };
        setChecklists(updatedChecklists);
    };

    const handleSave = async () => {
        setSaving(true);
        const result = await settingsService.updateSettings(SETTINGS_KEYS.OPPORTUNITY_CHECKLISTS, checklists);
        if (result.success) {
            toast.success('Settings saved successfully');
        } else {
            toast.error('Failed to save settings');
        }
        setSaving(false);
    };

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading settings...</div>;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
                        <p className="mt-1 text-sm text-gray-500">Manage application configurations.</p>
                    </div>
                </div>

                {/* Opportunity Checklists - Admin Only */}
                {userProfile?.role === 'Admin' && (
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <SafeIcon icon={LuListTodo} className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-medium text-gray-900">Opportunity Checklists</h2>
                                    <p className="text-sm text-gray-500">Customize the standard checklist items for each opportunity stage.</p>
                                </div>
                            </div>

                            {/* Stage Selector */}
                            <div className="flex overflow-x-auto">
                                {Object.values(OPPORTUNITY_STAGES).map(stage => (
                                    <button
                                        key={stage}
                                        onClick={() => setActiveStage(stage)}
                                        className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${activeStage === stage
                                            ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {stage}
                                    </button>
                                ))}
                            </div>

                            {/* Checklist Editor */}
                            <div className="bg-gray-50 rounded-b-lg  p-6 space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wider">
                                        {activeStage} Checklist
                                    </h3>
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        <SafeIcon icon={LuSave} className="mr-2 h-4 w-4" />
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>

                                <ul className="space-y-3">
                                    {(checklists[activeStage] || []).map((item, index) => (
                                        <li key={index} className="flex items-center space-x-2 animate-fadeIn">
                                            <div className="flex-grow">
                                                <input
                                                    type="text"
                                                    value={item}
                                                    onChange={(e) => handleUpdateItem(index, e.target.value)}
                                                    className="p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                />
                                            </div>
                                            <button
                                                onClick={() => handleRemoveItem(index)}
                                                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                                title="Remove item"
                                            >
                                                <SafeIcon icon={LuTrash2} className="h-5 w-5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                {/* Add New Item */}
                                <form onSubmit={handleAddItem} className="mt-4 flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={newItem}
                                        onChange={(e) => setNewItem(e.target.value)}
                                        placeholder="Add new checklist item..."
                                        className="p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!newItem.trim()}
                                        className="inline-flex items-center p-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:bg-gray-400"
                                    >
                                        <SafeIcon icon={LuPlus} className="h-5 w-5" />
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Security Settings (Change Password) */}
            <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <SafeIcon icon={LuLock} className="h-6 w-6 text-red-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-medium text-gray-900">Security</h2>
                            <p className="text-sm text-gray-500">Manage your account security.</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                        <div>
                            <h3 className="text-sm font-medium text-gray-900">Password</h3>
                            <p className="text-sm text-gray-500">Change your password to keep your account secure.</p>
                        </div>
                        <button
                            onClick={() => window.location.href = '/update-password'}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            Change Password
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
